import { useMemo } from 'react';
import type { FunctionComponent } from 'react';
import styled from 'styled-components';
import { Trans } from 'react-i18next';
import times from 'lodash/times';
import { PositionFromProps } from '../../utils/styledcomponents';
import type { PositionProps } from '../../utils/styledcomponents';
import ConceptsBarItem from './ConceptsBarItem';
import ActiveConceptsBarItem from './ActiveConceptsBarItem';
import NewConceptsBarItem from './NewConceptsBarItem';
import { getSeasonByIndex } from '../../data/Seasons';
import type { Round } from '../../store/PlaythroughSlice';

const Bar = styled.div`
	border: ${(props) => props.theme.borderWidth.bar} solid
		${(props) => props.theme.colors.darkBlue};
	background-color: ${(props) => props.theme.colors.blue};
	box-shadow: ${(props) => props.theme.shadow.bar};
	border-bottom-left-radius: 7.5vmin;
	border-bottom-right-radius: 7.5vmin;
	padding-top: 18.5vmin;
	padding-left: 2.2vmin;
	padding-right: 2.2vmin;
	padding-bottom: 2.2vmin;
	display: flex;
	flex-direction: column;
	align-items: stretch;
	font: ${(props) => props.theme.font.default};
	white-space: pre-line;
	will-change: transform;
`;

const Header = styled.div`
	color: ${(props) => props.theme.colors.white};
	display: flex;
	flex-direction: column;
	padding-bottom: 0.6vmin;
`;

const Title = styled.div`
	align-items: center;
	display: flex;
	flex-direction: row;
	font-size: 3.5vmin;
	justify-content: space-between;
`;

const Subtitle = styled.div`
	padding-top: 0.6vmin;
	padding-bottom: 0.6vmin;
	font-size: 1.5vmin;
	letter-spacing: 2px;
`;

const Items = styled.div`
	display: flex;
	flex-direction: column;
	align-items: stretch;
	& > * {
		margin: 0.4vmin;
	}
`;

const Item = styled.div`
	position: relative;
`;

interface Props {
	className?: string;
	roundCount: number;
	finishedRounds: Round[];
	finishedRoundsCount: number;
	onConcepts?: () => void;
}

const ConceptsBar: FunctionComponent<Props> = (props: Props) => {
	const {
		className,
		roundCount,
		finishedRounds,
		finishedRoundsCount,
		onConcepts
	} = props;

	const itemComponents = useMemo(() => {
		const getConceptItem = (index: number) => {
			const round = finishedRounds[index];
			const season = getSeasonByIndex(index);
			if (index === finishedRoundsCount) {
				return <NewConceptsBarItem season={season} onConcepts={onConcepts} />;
			} else if (round) {
				return <ActiveConceptsBarItem season={season} round={round} />;
			} else {
				return <ConceptsBarItem season={season} />;
			}
		};

		return times(roundCount, (index) => {
			return <Item key={index}>{getConceptItem(index)}</Item>;
		});
	}, [roundCount, finishedRounds, finishedRoundsCount, onConcepts]);

	return (
		<Bar className={className}>
			<Header>
				<Title>
					<Trans>concepts_bar.title</Trans>
				</Title>
				<Subtitle>
					<Trans>concepts_bar.subtitle</Trans>
				</Subtitle>
			</Header>
			<Items>{itemComponents}</Items>
		</Bar>
	);
};

export default styled(ConceptsBar)<PositionProps>(PositionFromProps);
